/**
 * CreditInfo - Credit information for a learner course.
 *
 * - `jurisdiction`: Full jurisdiction name (e.g., "New York") - used in flyout display
 * - `jurisdictionCode`: Abbreviation (e.g., "NY") - used in chip labels
 */
export interface CreditInfo {
  /** Full jurisdiction name (e.g., "New York") - used in flyout for readability */
  jurisdiction: string;
  /** Jurisdiction abbreviation (e.g., "NY") - used in chip labels */
  jurisdictionCode: string;
  creditType: string;
  amount: number;
  userMatch: boolean;
}

export interface LearnerCourse {
  id: string;
  title: string;
  description?: string;
  duration: number; // in minutes
  /**
   * Credential type codes the course offers (e.g. `['cle']`, `['cle', 'cpe']`).
   * Empty for non-credit content (Microlearning, Skills, Pathway). Drives the
   * row's credential chip(s); one chip is rendered per code.
   */
  credentialCodes: string[];
  /**
   * LearningObjectType code — used as the chip kind when `credentialCodes` is
   * empty (e.g. `'microlearning'`, `'skills'`, `'pathway'`). Ignored for
   * credit-bearing rows.
   */
  learningObjectTypeCode?: string;
  practiceAreas: string[];
  credits: CreditInfo[];
  progress?: number;
  status?: 'not_started' | 'in_progress' | 'completed';
  thumbnailUrl?: string;
  liveDateTime?: string; // ISO date string for live courses
  /** CourseLiveEvent.Id — present only for upcoming live events; enables the Add to Calendar affordance. */
  liveEventId?: number;
  isBookmarked?: boolean;
  /** Delivery track: "OnDemand" or "Live". When both tracks are active, two LearnerCourse items exist for the same course ID. */
  deliveryMode?: 'OnDemand' | 'Live';
  /** When an admin assigned the course to the learner. Null when self-started. */
  assignedAt?: string | null;
  /**
   * Original recording / live event date — when the course was actually delivered or
   * recorded. Distinct from when our catalog imported it. Drives the "Recorded …"
   * line on dashboard cards. Null for content with no original recording event.
   */
  originalLiveDate?: string | null;
}

export interface CourseListResponse {
  courses: LearnerCourse[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

export function formatCreditLabel(credit: CreditInfo): string {
  // Use abbreviation for chip labels (compact display)
  return `${credit.jurisdictionCode} ${credit.creditType} ${credit.amount}`;
}
